// re.rejouis.fr : IDENTIFIER PAR PHOTO (lab.rejouis.fr).
// Prend / choisit une photo → redimensionne côté client → fonction `scan`
// → propositions (catalogue éligible). Le client compare sa photo au modèle
// proposé et confirme ou refuse (2026-10-28) : les deux réponses partent au lab
// comme signal d'apprentissage. Confirmé → parcours produit (même chemin que la
// recherche). Ton humble : l'outil ne reconnaît pas tout, la vérification est au client.
import { useRef, useState } from 'react';
import { EcranHeader, Icons, Spinner, Illus } from '../components/ui.jsx';
import CarteProduit from '../components/CarteProduit.jsx';
import PanierBar from '../components/PanierBar.jsx';
import { identifierParPhoto, confirmerScan } from '../lib/api.js';
import { suivre } from '../lib/posthog.js';

const MAX_DIM = 1024; // côté max envoyé au lab (suffisant pour la reco)

// Redimensionne la photo en JPEG base64 (sans préfixe data:).
function preparerImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve({ base64: dataUrl.split(',')[1], apercu: dataUrl });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image illisible')); };
    img.src = url;
  });
}

const lien = { color: 'var(--rouge)', fontWeight: 600, padding: 0 };

export default function Scan({ go, panier, onChoisir }) {
  const [etat, setEtat] = useState('idle'); // idle | loading | ok | error
  const [apercu, setApercu] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [matches, setMatches] = useState([]);
  const [erreur, setErreur] = useState(null);
  // Proposition ouverte pour comparaison (le client vérifie avec son jouet).
  const [selection, setSelection] = useState(null);
  const [refuses, setRefuses] = useState(0);
  const inputRef = useRef(null);

  async function onFichier(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-choisir le même fichier
    if (!file) return;
    setEtat('loading');
    setErreur(null);
    setMatches([]);
    setSelection(null);
    setRefuses(0);
    try {
      const { base64, apercu: ap } = await preparerImage(file);
      setApercu(ap);
      setImageBase64(base64);
      const r = await identifierParPhoto(base64, 'image/jpeg');
      const scanMatches = r.matches || [];
      suivre('photo_scan_completed', { match_count: scanMatches.length });
      setMatches(scanMatches);
      setEtat('ok');
    } catch (err) {
      setErreur(err.message);
      setEtat('error');
    }
  }

  // Retour du client (apprentissage, non bloquant) : photo + modèle proposé vers le lab,
  // « confirme » dans la file à valider, « refuse » rangé à part.
  function signal(match, verdict) {
    if (imageBase64 && match?.produit?.id) {
      confirmerScan(imageBase64, match.produit.id, match.score, verdict).catch(() => { /* best effort */ });
    }
  }

  function confirmer(match) {
    suivre('photo_scan_match_selected', { match_score: match.score, verdict: 'confirme' });
    signal(match, 'confirme');
    onChoisir(match.produit);
  }

  function refuser(match) {
    suivre('photo_scan_match_refused', { match_score: match.score });
    signal(match, 'refuse');
    setMatches((ms) => ms.filter((m) => m.produit.id !== match.produit.id));
    setSelection(null);
    setRefuses((n) => n + 1);
  }

  // « Aucun ne correspond » : la première proposition est notée refusée, puis recherche par nom.
  function aucun() {
    if (matches[0]) {
      suivre('photo_scan_match_refused', { match_score: matches[0].score, aucun: true });
      signal(matches[0], 'refuse');
    }
    go('catalogue', { focus: true });
  }

  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '16px 0 110px' }}>
      <EcranHeader titre="Identifier par photo" onBack={() => go('accueil')} />

      <div style={{ padding: '2px 16px 0' }}>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFichier} style={{ display: 'none' }} />

        {apercu ? (
          <div className="dropzone rempli" onClick={() => etat !== 'loading' && inputRef.current?.click()} style={{ padding: 10 }}>
            <div className="dz-thumb">
              <img src={apercu} alt="Ta photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--stone)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icons.camera size={15} /> Reprendre une photo
            </div>
          </div>
        ) : (
          <>
            <div className="dropzone" onClick={() => inputRef.current?.click()}>
              <div style={{ position: 'relative' }}>
                <Illus nom="photo" size={60} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 14.5 }}>Prends ton jouet en photo</div>
              <div style={{ fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5, maxWidth: 270 }}>
                Seul, sur fond neutre et bien éclairé : on te propose les modèles qui lui ressemblent.
              </div>
            </div>
            <div style={{ padding: '12px 2px 0', fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5 }}>
              Notre outil apprend avec la pratique : il ne reconnaît pas encore tous les jouets.
              Tu connais son nom ou tu as gardé la boîte ?{' '}
              <button onClick={() => go('catalogue', { focus: true })} style={lien}>La recherche ou le code-barres</button>{' '}
              sont plus sûrs.
            </div>
          </>
        )}
      </div>

      {etat === 'loading' ? (
        <div style={{ padding: '20px 16px', display: 'flex', justifyContent: 'center' }}>
          <Spinner size={20} label="Recherche de modèles proches…" />
        </div>
      ) : null}

      {etat === 'error' ? (
        <div style={{ padding: '14px 16px 0' }}>
          <div className="soft-card" style={{ padding: 16, fontSize: 13, color: 'var(--stone)', lineHeight: 1.5 }}>
            L’identification n’a pas abouti ({erreur}). Réessaie avec une autre photo,
            ou cherche ton modèle par son nom.
          </div>
          <button className="btn btn-secondary btn-block" style={{ marginTop: 10 }} onClick={() => go('catalogue', { focus: true })}>
            Chercher par nom
          </button>
        </div>
      ) : null}

      {/* Comparaison : sa photo à côté du modèle proposé, puis oui / non */}
      {etat === 'ok' && selection ? (
        <div className="anim-pop" style={{ padding: '18px 16px 0' }}>
          <div className="t-overline" style={{ marginBottom: 8 }}>Est-ce bien ton jouet ?</div>
          <div className="card" style={{ padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ textAlign: 'center' }}>
                <img src={apercu} alt="Ta photo" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 12, background: 'var(--stone-pale)' }} />
                <div style={{ fontSize: 11.5, color: 'var(--stone)', marginTop: 4 }}>Ta photo</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                {selection.produit.image ? (
                  <img src={selection.produit.image} alt={selection.produit.titre} style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: 12, background: 'var(--stone-pale)' }} />
                ) : <div style={{ width: '100%', aspectRatio: '1', borderRadius: 12, background: 'var(--stone-pale)' }} />}
                <div style={{ fontSize: 11.5, color: 'var(--stone)', marginTop: 4 }}>Modèle proposé</div>
              </div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 14.5, marginTop: 12 }}>{selection.produit.titre}</div>
            {selection.produit.marque ? <div style={{ fontSize: 12.5, color: 'var(--stone)' }}>{selection.produit.marque}</div> : null}
            <div style={{ fontSize: 12.5, color: 'var(--encre-doux)', lineHeight: 1.5, marginTop: 10 }}>
              Compare la marque, la forme, la taille et les boutons. C’est à toi de vérifier que ce modèle est bien celui
              que tu envoies : si le jouet reçu ne correspond pas, le crédit est annulé.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary btn-block" onClick={() => confirmer(selection)}>Oui, c’est le mien</button>
              <button className="btn btn-secondary btn-block" onClick={() => refuser(selection)}>Non, ce n’est pas lui</button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--stone)', textAlign: 'center', marginTop: 10 }}>
              Ta réponse aide l’outil à mieux reconnaître les jouets.
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button onClick={() => setSelection(null)} style={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 600 }}>Revenir aux propositions</button>
          </div>
        </div>
      ) : null}

      {etat === 'ok' && !selection ? (
        matches.length > 0 ? (
          <>
            {refuses ? (
              <div style={{ padding: '14px 16px 0', fontSize: 12.5, color: 'var(--stone)' }}>Merci, c’est noté : ça aide l’outil à progresser.</div>
            ) : null}
            <div className="t-overline" style={{ padding: '18px 16px 4px' }}>
              {matches.length === 1 ? 'Une piste à vérifier' : `${matches.length} pistes à vérifier`}
            </div>
            <div style={{ padding: '0 16px 10px', fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5 }}>
              Ce sont des propositions, pas une certitude. Touche celle qui ressemble à ton jouet pour la comparer.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px' }}>
              {matches.map((m) => (
                <CarteProduit key={m.produit.id} produit={m.produit} dansPanier={false} onChoisir={() => setSelection(m)} />
              ))}
            </div>
            <div style={{ padding: '14px 16px 0', textAlign: 'center', fontSize: 12.5, color: 'var(--stone)' }}>
              Aucun ne correspond ?{' '}
              <button onClick={aucun} style={lien}>Cherche par nom</button>
            </div>
          </>
        ) : (
          <div className="anim-pop" style={{ padding: '14px 16px 0' }}>
            <div className="soft-card" style={{ padding: 18, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{refuses ? 'Merci, c’est noté' : 'On ne l’a pas reconnu'}</div>
              <div style={{ fontSize: 13, color: 'var(--stone)', margin: '6px 0 14px', lineHeight: 1.5 }}>
                {refuses ? 'Ta réponse aide l’outil à progresser. ' : 'L’outil ne connaît pas encore tous les jouets. '}
                Cherche ton modèle par son nom ou son code-barres, c’est le plus sûr.
              </div>
              <button className="btn btn-primary btn-block" onClick={() => go('catalogue', { focus: true })}>
                Chercher par nom
              </button>
              <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 10, lineHeight: 1.5 }}>
                Introuvable dans notre catalogue ?{' '}
                <button onClick={() => go('lot')} style={lien}>Propose-le dans un lot</button>
                , même un seul jouet.
              </div>
            </div>
          </div>
        )
      ) : null}

      <PanierBar panier={panier} go={go} />
    </div>
  );
}
